/**
 * Family Pulse status hooks (PERSONAL accounts).
 *
 * `useFamilyStatuses` reads every member's current status (GET /family/status).
 * `useSetFamilyStatus` writes the current user's own status through the generic
 * user-preferences store (context = FAMILY_STATUS, USER scope) and then
 * invalidates the family-status query so the roster reflects the change.
 */

import { useCallback } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FamilyStatusPreference } from '@scrambl/shared';
import { PreferenceContextEnum, PreferenceScopeEnum } from '@scrambl/shared';

import { familyStatusApi } from '@/lib/api/family-status.api';
import { useOrganizationStore } from '@/store/organization.store';
import { useUserPreferencesStore } from '@/store/user-preferences.store';

import { queryKeys } from '../query-keys';

/** GET /family/status — one entry per active member. */
export function useFamilyStatuses(options?: { enabled?: boolean }) {
  const { currentOrganization } = useOrganizationStore();
  return useQuery({
    queryKey: queryKeys.family.status(),
    queryFn: () => familyStatusApi.getStatuses(),
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    // Status changes often but is cheap; keep it fresh without hammering.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Write the current user's own status. Pass an empty payload (or nulls) to
 * clear it. Returns a mutation whose `mutateAsync` accepts the payload.
 */
export function useSetFamilyStatus() {
  const queryClient = useQueryClient();
  const savePreference = useUserPreferencesStore(state => state.savePreference);

  const write = useCallback(
    async (status: FamilyStatusPreference) => {
      await savePreference(
        PreferenceContextEnum.FAMILY_STATUS,
        status as Record<string, unknown>,
        undefined,
        PreferenceScopeEnum.USER,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.family.status() });
    },
    [savePreference, queryClient],
  );

  return useMutation({ mutationFn: write });
}
